import React from 'react';
import { Box, Typography, Button, IconButton, Link, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ModalPayment from './ModalPayment';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useAdmin } from '../../contexts/AdminContext';

const PaymentManagement = () => {
  const { payments, deletePayment } = useAdmin();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [paymentToDelete, setPaymentToDelete] = React.useState(null);
  const [resultModalOpen, setResultModalOpen] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState('');
  const [resultSeverity, setResultSeverity] = React.useState('success');
  const [noteModalOpen, setNoteModalOpen] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState('');

  const handlePaymentCreated = (newPayment) => {
    // Il context si aggiornerà automaticamente
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deletePayment(paymentToDelete._id);

      if (result.success) {
        setDeleteModalOpen(false);
        setPaymentToDelete(null);

        // Show success message
        setResultMessage('Pagamento rimosso con successo');
        setResultSeverity('success');
        setResultModalOpen(true);
      } else {
        // Show error message
        setResultMessage('Errore durante la rimozione del pagamento');
        setResultSeverity('error');
        setResultModalOpen(true);

        setDeleteModalOpen(false);
        setPaymentToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);

      // Show error message
      setResultMessage('Errore durante la rimozione del pagamento');
      setResultSeverity('error');
      setResultModalOpen(true);

      setDeleteModalOpen(false);
      setPaymentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setPaymentToDelete(null);
  };

  const handleResultClose = () => {
    setResultModalOpen(false);
  };

  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setNoteModalOpen(true);
  };

  const handleNoteModalClose = () => {
    setNoteModalOpen(false);
    setSelectedNote('');
  };

  const handleCourseClick = (courseId) => {
    if (courseId) {
      navigate(`/teacher/course/${courseId}`);
    }
  };

  const paymentColumns = [
    // { field: 'stripePaymentId', headerName: 'ID Pagamento', flex: 1.5, minWidth: 160, resizable: true },
    {
      field: 'fullName',
      headerName: 'Nome Completo',
      flex: 1.2,
      minWidth: 140,
      resizable: true,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'courseName',
      headerName: 'Corso',
      flex: 1.2,
      minWidth: 140,
      resizable: true,
      renderCell: (params) => {
        const courseId = params.row.course?._id;
        const courseName = params.value;

        return (
          <Link
            component='button'
            variant='body2'
            onClick={() => handleCourseClick(courseId)}
            sx={{
              color: '#1976d2', // Classic blue link color
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                color: '#1565c0', // Darker blue on hover
                textDecoration: 'underline',
              },
            }}
          >
            {courseName}
          </Link>
        );
      },
    },
    { field: 'amount', headerName: 'Importo', flex: 0.7, minWidth: 90, resizable: true, renderCell: (params) => `€${params.value}` },
    {
      field: 'createdAt',
      headerName: 'Data',
      flex: 0.8,
      minWidth: 110,
      resizable: true,
      renderCell: (params) => (params.value ? dayjs(params.value).locale('it').format('DD-MMM-YY HH:mm') : 'N/A'),
    },
    {
      field: 'paymentType',
      headerName: 'Tipo',
      flex: 0.7,
      minWidth: 90,
      resizable: true,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'note',
      headerName: 'Note',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => {
        const note = params.value;
        if (!note) {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        }

        const preview = note.length > 30 ? `${note.substring(0, 30)}...` : note;

        return (
          <Tooltip title={note} arrow placement='top'>
            <Box
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                },
                p: 0.5,
                borderRadius: 1,
              }}
              onClick={() => handleNoteClick(note)}
            >
              <Typography
                variant='body2'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  color: '#1565c0',
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.dark',
                    textDecoration: 'underline',
                  },
                }}
              >
                {preview}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => (
        <Box>
          {/* <IconButton size="small" color="primary">
            <Visibility />
          </IconButton> */}
          <IconButton size='small' color='error' onClick={() => handleDeleteClick(params.row)}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Adatta i dati per DataGrid: aggiungi id e i nuovi campi richiesti
  // console.log('payments', payments);
  const rows = payments.map((p) => ({
    ...p,
    id: p._id,
    fullName: `${p.user?.name || p.userId?.name || 'N/A'} ${p.user?.surname || p.userId?.surname || 'N/A'}`.trim(),
    courseName: p.course?.name,
    // Mantieni i dati originali per la navigazione
    enrollment: p.enrollment,
    enrollmentId: p.enrollmentId,
  }));

  let dataGridError = null;
  let dataGridContent = null;
  try {
    dataGridContent = (
      <DataGrid
        rows={rows}
        columns={paymentColumns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        hideFooterPagination={payments.length <= 10}
      />
    );
  } catch (err) {
    dataGridError = err;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>Storico Pagamenti</Typography>
        <Button variant='contained' startIcon={<Add />} onClick={() => setModalOpen(true)}>
          Aggiungi Pagamento
        </Button>
      </Box>
      {dataGridError ? (
        <Box sx={{ color: 'red', p: 2, border: '1px solid red', borderRadius: 2 }}>
          <Typography variant='body1' color='error'>
            Errore nella DataGrid: {dataGridError.message}
          </Typography>
          <pre style={{ fontSize: 12, color: 'red' }}>{dataGridError.stack}</pre>
        </Box>
      ) : (
        dataGridContent
      )}
      <ModalPayment open={modalOpen} onClose={() => setModalOpen(false)} onPaymentCreated={handlePaymentCreated} />
      <ModalConfirm open={deleteModalOpen} title='Conferma Rimozione' message={`Sei sicuro di voler rimuovere il pagamento di €${paymentToDelete?.amount} per ${paymentToDelete?.fullName || 'questo utente'}? Questa azione non può essere annullata.`} onConfirm={handleDeleteConfirm} onCancel={handleDeleteCancel} />
      <ModalResult open={resultModalOpen} message={resultMessage} severity={resultSeverity} onClose={handleResultClose} />
      <Dialog open={noteModalOpen} onClose={handleNoteModalClose} maxWidth='sm' fullWidth disableEscapeKeyDown>
        <DialogTitle>Note Pagamento</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {selectedNote}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoteModalClose} variant='contained' color='primary'>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentManagement;
